import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import ResultAndRevision from './ResultAndRevision';
import { AuthContext } from './AuthContext';
import './styles/MainContent.scss';
import { compressImage } from './utils/imageCompression';
import CategorySelection, { getCategoryIcon, categories, FILIOCHT_POEMS } from './components/CategorySelection';
import QuestionSelect from './components/QuestionSelect';
import { track } from '@vercel/analytics';
import MathWorkflow from './components/math/MathWorkflow';
import './components/math/MathWorkflow.scss';
import logger from './utils/logger';
import { checkIrishSpelling, highlightMisspelledWords, getPlainTextFromHighlighted } from './utils/spellchecker';
import ComprehensionView from './components/ComprehensionView';
import ComprehensionSubmissionsView from './components/ComprehensionResults';
import ComprehensionUpload from './components/ComprehensionUpload';
import QuizWorkflow from './components/QuizWorkflow'; // Add this import
import StudentNotesWorkflow from './components/StudentNotesWorkflow';


function MainContent({
  // Props required for all steps:
  step,
  setStep,
  category,
  setCategory,
  question,
  setQuestion,
  usageRemaining,
  setUsageRemaining,
  handleQuestionSubmit,
  prevStep,
  nextStep,
  // imageFiles,
  handleRemoveImage,
  answer,
  setAnswer,
  handleAnswerChange,
  result,
  setResult,
  handleResetApp,
  feedbackSubmitted,
  handleFeedbackSubmit,
  feedbackCategory,
  setFeedbackCategory,
  feedbackText,
  setFeedbackText,
  handleSavePDF,
  isLoading,
  setIsLoading,
  authToken,
  handleImageFiles,
  API_BASE_URL,
  onConfirmAnswer,
  userData,
  savedEssayData,
  clearSavedEssayData,
  shortTaskId,
  setShortTaskId,
  dbTaskId,
  selectedBranch,
  setSelectedBranch,
  currentQuizId, // New prop from App.js
  currentAttemptId, // New prop from App.js
  clearQuizState // New prop from App.js
}) {
  const [error, setError] = React.useState(null);
  const [isRevisionMode, setIsRevisionMode] = useState(false);
  const [paragraphs, setParagraphs] = useState([]);
  const [feedbackBlocks, setFeedbackBlocks] = useState([]);
  const [savedEssays, setSavedEssays] = useState([]);
  const [isRevising, setIsRevising] = useState(false);
  const [editedSections, setEditedSections] = useState({});
  const [feedbackType, setFeedbackType] = useState('inline');  // Always 'inline' by default
  const [selectedFiliocht, setSelectedFiliocht] = useState(null);
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [spellCheckEnabled, setSpellCheckEnabled] = useState(false);
  const [imageFiles, setImageFiles] = React.useState([]);
  const [essayId, setEssayId] = useState(null);
  const [teacherFeedback, setTeacherFeedback] = useState(null); 
  const { handleApiError } = useContext(AuthContext);
  const [grammarWarning, setGrammarWarning] = useState(null);
  const [grammarCheckInProgress, setGrammarCheckInProgress] = useState(false);
  const [spellCheckResults, setSpellCheckResults] = useState(null);
  const [isSpellChecking, setIsSpellChecking] = useState(false);
  const [highlightedAnswer, setHighlightedAnswer] = useState('');
  const [shareWithTeacher, setShareWithTeacher] = useState(false);
  // Add state for custom confirm dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogCallback, setConfirmDialogCallback] = useState(null);
  const [ignoreFadaEnabled, setIgnoreFadaEnabled] = useState(false);
  const [grammarExemption, setGrammarExemption] = useState(false);
  const [userChangedExemption, setUserChangedExemption] = useState(false);
  const [tempMessage, setTempMessage] = useState(null);
  // Add word count state
  const [wordCount, setWordCount] = useState(0);

  // Lifted state for comprehension creation
  const [isCreatingComprehension, setIsCreatingComprehension] = useState(false);

  // Add state to track the submission being viewed
  const [viewingSubmissionData, setViewingSubmissionData] = useState(null);

  // Add effect to update grammar exemption when userData changes, but only if user hasn't manually changed it
  useEffect(() => {
    if (userData && !userChangedExemption) {
      logger.debug('Setting grammar exemption from user data:', userData.grammar_exemption);
      setGrammarExemption(userData.grammar_exemption || false);
    }
  }, [userData, userChangedExemption]);

  // Strengthen the step 4 initialization to ensure checkbox gets checked for users with exemptions
  useEffect(() => {
    if (step === 4) {
      logger.debug('Step 4 entered - current grammarExemption state:', grammarExemption);
      logger.debug('Step 4 entered - userData grammar exemption setting:', userData?.grammar_exemption);
      
      // Only set from user data if the user hasn't manually changed it
      if (userData?.grammar_exemption && !userChangedExemption) {
        logger.debug('Pre-checking grammar exemption checkbox based on user settings');
        // Use setTimeout to ensure this happens after render
        setTimeout(() => {
          setGrammarExemption(true);
        }, 0);
      }
      
      // Reset to user preference when entering step 4 and for non-English categories
      const isEnglishCategory = category === 'Poetry' || 
                               category === 'Shakespeare' || 
                               category === 'Comparative' || 
                               category === 'Composition' ||
                               (typeof category === 'string' && category.startsWith('GCSE_'));
                               
      if (!isEnglishCategory) {
        // Reset grammar exemption when not in English category
        logger.debug('Not an English category, disabling grammar exemption checkbox');
        setGrammarExemption(false);
        setUserChangedExemption(false); // Reset the user change flag for non-English categories
      }
    }
  }, [step, userData, category, userChangedExemption]);

  // Also add an effect to reset when category changes
  useEffect(() => {
    // When category changes, reset exemption state based on user preferences for English categories
    const isEnglishCategory = category === 'Poetry' || 
                             category === 'Shakespeare' || 
                             category === 'Comparative' || 
                             category === 'Composition' ||
                             (typeof category === 'string' && category.startsWith('GCSE_'));
    
    if (isEnglishCategory && userData?.grammar_exemption && !userChangedExemption) {
      logger.debug('Category changed to English with user grammar exemption enabled - updating checkbox');
      setGrammarExemption(true);
    } else if (!isEnglishCategory) {
      // Reset grammar exemption when not in English category
      logger.debug('Category changed to non-English - disabling grammar exemption checkbox');
      setGrammarExemption(false);
      setUserChangedExemption(false); // Reset the user change flag for non-English categories
    }
  }, [category, userData, userChangedExemption]);

  // Determine subject based on category - moved to component level
  const getCategorySubject = (category) => {
    if (!category) return 'English'; // Default to English if category is null/undefined
    
    // Map category to subject
    if (category === 'Shakespeare' || category === 'Poetry' || 
        category === 'Comparative' || category === 'Composition' ||
        category.startsWith('GCSE_')) {
      return 'English';
    } else if (category === 'Aiste' || category === 'Grammar Checker' || 
              category === 'IE_Oral' || category.startsWith('Filiocht')) {
      return 'Irish';
    } else if (category.startsWith('FR_')) {
      return 'French';
    } else if (category.startsWith('ES_')) {
      return 'Spanish';
    } else if (category.startsWith('DE_')) {
      return 'German';
    } else if (category.startsWith('MATH_')) {
      return 'Maths';
    } else {
      return 'English'; // Default to English
    }
  };

  const stripInlineAnnotations_for_confirm_answer = (text) => {
    // First replace double-bracketed annotations with just their content
    const withoutDoubleBrackets = text.replace(/\{\{[PCLMGSE]:(.*?)\}\}/g, '$1');
    // Then remove single-bracketed annotations completely
    return withoutDoubleBrackets.replace(/\{[PCLMGSE]:[^}]+\}\n*\n*/g, '').replace(/\\n/g, '\n');
  };
    
  const handleSubmitAnswer = async () => {
    // Track submission attempt
    track('answer_submitted', { 
      hasText: !!answer.trim(),
      imageCount: imageFiles.length,
      category: category
    });

    if (!answer.trim() && imageFiles.length === 0) {
      return alert('Please provide an answer or upload an image.');
    }

    setIsLoading(true);

    try {
      if (imageFiles.length > 0) {
        const formData = new FormData();
        
        // Add category to formData
        if (category) {
          formData.append("category", category);
        }
        
        // Process each file individually
        for (let i = 0; i < imageFiles.length; i++) {
          const originalFile = imageFiles[i];
          
          // Compress the image (if it wasn't already compressed)
          const compressedFile = await compressImage(originalFile);
          
          // Generate a unique filename
          const timestamp = Date.now();
          const randomStr = Math.random().toString(36).substring(2, 10);
          const fileExtension = originalFile.name.split('.').pop();
          const uniqueName = `page_${i+1}_${timestamp}_${randomStr}.${fileExtension}`;
          
          // Create a new File object
          const uniqueFile = new File(
            [compressedFile], 
            uniqueName,
            { type: compressedFile.type, lastModified: timestamp }
          );
          
          // Use standard field name with unique filenames
          formData.append("images", uniqueFile);
          
          logger.debug(`Submitting file ${i+1}: ${uniqueFile.name} (${(uniqueFile.size / 1024 / 1024).toFixed(2)} MB)`);
        }
        
        logger.debug("Files being uploaded (before compression):", imageFiles);
        logger.debug("FormData entries (after compression):", [...formData.entries()]);
        
        // Calculate timeout: max of 60 seconds or 20 seconds per page
        const timeoutSeconds = Math.max(60, imageFiles.length * 30);
        const timeoutMs = timeoutSeconds * 1000;
        
        // Initial OCR request
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ocr-images`, {
          method: 'POST',
          headers: { 
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          },
          body: formData,
          signal: AbortSignal.timeout(timeoutMs)
        });

        if (!response.ok) {
          const errorText = await response.text();
          logger.error('OCR Error:', response.status, errorText);
          throw new Error(`OCR failed: ${errorText}`);
        }

        const imgData = await response.json();
        
        // Poll for OCR results
        if (imgData.task_id) {
          const result = await pollOCRStatus(imgData.task_id);
          if (result.status === 'success') {
            setAnswer(result.response);
            if (result.usageRemaining !== undefined) {
              setUsageRemaining(result.usageRemaining);
            }
          } else {
            throw new Error('OCR processing failed');
          }
        }
      }
      setIsLoading(false);
      nextStep();
    } catch (error) {
      logger.error(error);
      alert('There was a problem processing your images. Please try again.');
      setIsLoading(false);
    }
  };

  const pollOCRStatus = async (taskId) => {
    const maxAttempts = 90; // Maximum number of polling attempts
    let attempts = 0;

    while (attempts < maxAttempts) {
      attempts++;
      
      // Calculate backoff interval with exponential growth
      // Start at 2s, increase to max 10s with jitter
      const baseInterval = Math.min(2000 * Math.pow(1.2, Math.floor(attempts / 5)), 10000);
      const interval = baseInterval * (0.8 + Math.random() * 0.4); // Add jitter
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ocr-status/${taskId}`,
          {
            headers: { 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}` }
          }
        );
        
        const data = await response.json();
        
        // Only log status periodically to reduce noise
        if (attempts === 1 || attempts % 5 === 0) {
          logger.info(`OCR Status (attempt ${attempts}):`, data.state);
        }
        
        if (data.state === 'SUCCESS') {
          return data.result;
        } else if (data.state === 'FAILURE') {
          throw new Error('OCR processing failed');
        }
        
        // Wait with exponential backoff
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (error) {
        // If there's a network error, wait before retrying
        logger.error(`OCR polling error (attempt ${attempts}):`, error);
        await new Promise(resolve => setTimeout(resolve, interval * 2)); // Double the interval on error
        
        // If we've had multiple consecutive errors, consider throwing
        if (attempts % 3 === 0) {
          throw new Error(`OCR status check failed after ${attempts} attempts: ${error.message}`);
        }
      }
    }
    
    throw new Error('OCR processing timed out');
  };
  
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsLoading(true);
    
    try {
      // Create a new FormData object for each request to avoid any potential issues
      const formData = new FormData();
      
      // Add category to formData
      if (category) {
        formData.append("category", category);
      }
      
      // Array to hold compressed files
      const compressedFiles = [];
      
      // Process each file individually with detailed logging
      for (let i = 0; i < files.length; i++) {
        const originalFile = files[i];
        logger.debug(`Original file ${i+1}: ${originalFile.name} (${(originalFile.size / 1024 / 1024).toFixed(2)} MB)`);
        
        // Compress the image
        const compressedFile = await compressImage(originalFile);
        
        // Generate a unique filename with timestamp and sequential number
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 10);
        const fileExtension = originalFile.name.split('.').pop();
        const uniqueName = `page_${i+1}_${timestamp}_${randomStr}.${fileExtension}`;
        
        // Create a completely new File object to break any reference to the original
        const uniqueFile = new File(
          [compressedFile], 
          uniqueName,
          { type: compressedFile.type, lastModified: timestamp }
        );
        
        // Use the standard field name 'images' but with unique filenames
        formData.append("images", uniqueFile);
        
        // Store the compressed file
        compressedFiles.push(uniqueFile);
        
        logger.debug(`Compressed file ${i+1}: ${uniqueFile.name} (${(uniqueFile.size / 1024 / 1024).toFixed(2)} MB)`);
      }
      
      // Log all entries in formData for debugging
      logger.debug("FormData entries:");
      let entryIndex = 0;
      for (let pair of formData.entries()) {
        logger.debug(`Entry ${entryIndex++}: field=${pair[0]}, filename=${pair[1].name}, size=${(pair[1].size / 1024 / 1024).toFixed(2)} MB`);
      }
      
      // Upload the images
      logger.debug("Auth token being sent:", authToken);
      
      const response = await fetch(`${API_BASE_URL}/api/ocr-images`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload images');
      }

      const data = await response.json();
      
      // Store the compressed files
      const existingFiles = [...imageFiles];
      compressedFiles.forEach(file => existingFiles.push(file));
      handleImageFiles(existingFiles);
      
      if (data.task_id) {
        logger.info('Upload successful, task ID:', data.task_id);
      }
    } catch (error) {
      logger.error('Upload error:', error);
      setError(error.message);
    } finally {
      // Clear the file input
      event.target.value = '';
      setIsLoading(false);
    }
  };

  const processInlineFeedback = (feedback) => {
    if (!feedback) return '';
    
    // First, clean up excessive newlines
    let processed = feedback.replace(/\n{3,}/g, '\n\n');
    
    // Convert feedback annotations to colored spans
    const colorMap = {
      'P': '#2ecc71', // Green for Purpose
      'C': '#e74c3c', // Red for Coherence
      'L': '#3498db', // Blue for Language
      'M': '#f1c40f'  // Yellow for Mechanics
    };

    // Replace inline annotations {{P: text}} with colored spans
    processed = processed.replace(/\{\{([PCLM]):\s*([^}]+?)\}\}/g, (match, type, content) => {
      return `<span style="background-color: ${colorMap[type]}33; border-bottom: 2px solid ${colorMap[type]};">${content}</span>`;
    });

    // Replace feedback comments {P: text} with colored divs
    processed = processed.replace(/\{([PCLM]):\s*([^}]+?)\}/g, (match, type, content) => {
      return `<div style="margin: 5px 0; padding: 8px; background-color: ${colorMap[type]}22; border-left: 4px solid ${colorMap[type]};">${content}</div>`;
    });

    return processed;
  };

  // Custom confirm dialog component
  const CustomConfirmDialog = () => {
    if (!showConfirmDialog) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ marginTop: 0, color: '#e74c3c' }}>
            <span style={{ marginRight: '8px' }}>⚠️</span>
            Rabhadh
          </h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{confirmDialogMessage}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button 
              onClick={() => {
                setShowConfirmDialog(false);
                // Reset grammarCheckInProgress when user clicks Go Back
                setGrammarCheckInProgress(false);
                // No additional action needed for cancel
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go Back
            </button>
            <button 
              onClick={() => {
                setShowConfirmDialog(false);
                if (confirmDialogCallback) {
                  confirmDialogCallback();
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Submit Anyway
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Custom confirm function
  const customConfirm = (message, callback) => {
    setConfirmDialogMessage(message);
    setConfirmDialogCallback(() => callback);
    setShowConfirmDialog(true);
  };

  const handleConfirmClick = async (feedbackType) => {
    // Prevent multiple submissions
    if (isLoading) {
      logger.info("Submission already in progress, ignoring click");
      return;
    }
    
    // For Irish categories, check grammar first
    const isIrishCategory = category === 'Aiste' || 
                        category === 'Grammar Checker' || 
                        category === 'IE_Oral' || 
                        category.startsWith('Filiocht_');
    
    // Get subject from category
    const subject = getCategorySubject(category);
    
    if (isIrishCategory) {
      // Check for excessive spelling errors
      const cleanedText = stripInlineAnnotations_for_confirm_answer(answer);
      
      try {
        setIsLoading(true);
        // Use our existing checkIrishSpelling function
        const spellcheckResults = await checkIrishSpelling(cleanedText, ignoreFadaEnabled);
        const errorCount = spellcheckResults.results?.length || 0;
        
        logger.debug(`Spelling check complete. Found ${errorCount} spelling errors.`);
        
        // If more than 50 spelling errors, show a warning
        if (errorCount > 50) {
          logger.debug("Found excessive spelling errors:", errorCount);
          const warningMessage = "Rabhadh: This text contains " + errorCount + " spelling errors or non-Irish words, which is unusually high. We've spotted a large number of misspellings or non-Irish words in your scanned text. Please go back and fix some of these errors before getting feedback, otherwise it will take a long time to process your results. If the typos are mainly missing fadas, then please make sure \"ignore fadas\" is selected before going ahead. Pulc will then ignore any missing fadas in the text.";

          setGrammarWarning(warningMessage);
          setIsLoading(false); // Turn off loading while waiting for user decision
          
          // Use custom confirm dialog instead of window.confirm
          customConfirm(warningMessage, () => {
            logger.info("User confirmed to proceed despite spelling errors warning");
            // Clear the warning to prevent it from showing again
            setGrammarWarning(null);
            setIsLoading(true);
            onConfirmAnswer(feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, subject);
          });
          // Ensure we reset this flag since we're suspending the process
          setGrammarCheckInProgress(false);
          return; // Exit early after showing dialog
        }
        setIsLoading(false);
      } catch (error) {
        logger.error("Error checking spelling:", error);
        // Continue with submission even if spell check fails
        setIsLoading(false);
      }
      
      setIsLoading(true); // Set loading initially before check
      try {
        // No longer need a separate try/catch here for the check itself
        const grammarCheckResult = await checkIrishGrammarIssues(); // Await the result object

        if (!grammarCheckResult.proceed) {
          // Issues found (either grammar warning or API error after retries)
          // Set the banner warning regardless of whether we show the dialog
          if (grammarCheckResult.message) {
            setGrammarWarning(grammarCheckResult.message);
          }

          // Check if we should show the blocking dialog (high issue count OR API error)
          const shouldShowDialog = grammarCheckResult.issueCount === null || (grammarCheckResult.issueCount || 0) > 50;

          if (shouldShowDialog) {
             setIsLoading(false); // Turn off loading while dialog is shown

             customConfirm(grammarCheckResult.message, () => { // Use the message from the result
               // User confirmed, continue with submission
               logger.info("User confirmed to proceed despite grammar/error warning");
               setIsLoading(true); // Turn loading back on for submission
               setGrammarWarning(null); // Clear banner warning if it was set
               onConfirmAnswer(feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, subject);
             });
             setGrammarCheckInProgress(false); // Reset this flag as the process paused for the dialog
             return; // Stop execution here until dialog is resolved
          }
          else {
             // Low issue count, but warningNeeded was true - proceed with submission but keep banner
             logger.info(`Proceeding with submission despite low grammar issue count (${grammarCheckResult.issueCount}). Banner shown.`);
             // We already set the banner above. No need to set loading off/on.
             onConfirmAnswer(feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, subject);
             // Ensure loading/grammar check flags are correct (should already be true/false)
             setIsLoading(true);
             setGrammarCheckInProgress(false);
          }

        } else {
          // No issues, proceed normally
          setGrammarWarning(null); // Clear any previous banner warning
          // Don't need to call setGrammarCheckInProgress(false) here as it should be false already if successful
          onConfirmAnswer(feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, subject);
        }
      } catch (error) {
         // This outer catch is now only for unexpected errors *within handleConfirmClick itself*,
         // not errors from checkIrishGrammarIssues which are handled internally.
         logger.error("Unexpected error during submission process:", error);
         setIsLoading(false);
         setGrammarCheckInProgress(false);
         alert("An unexpected error occurred during submission. Please try again.");
      }
    } else {
      // Not an Irish category, proceed normally
      setIsLoading(true);
      onConfirmAnswer(feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, subject);
    }
  };

  // Function to split result into paragraphs and feedback
  const splitIntoParasAndFeedback = (text) => {
    const blocks = text.split(/(?={{[PCLM],}})/);
    const paras = [];
    const feedback = [];
    
    for (let i = 0; i < blocks.length; i++) {
      if (i % 2 === 0) {
        // Remove any feedback markers from the paragraph text
        const cleanPara = blocks[i].replace(/{{[PCLM],\s*|}}/g, '');
        paras.push(cleanPara.trim());
      } else {
        feedback.push(blocks[i].trim());
      }
    }
    
    return { paras, feedback };
  };

  // Function to handle starting revision mode
  const handleStartRevision = () => {
    const { paras, feedback } = splitIntoParasAndFeedback(result);
    setParagraphs(paras);
    setFeedbackBlocks(feedback);
    setIsRevisionMode(true);
  };

  // Function to handle paragraph edits
  const handleParagraphEdit = (index, newText) => {
    const newParagraphs = [...paragraphs];
    newParagraphs[index] = newText;
    setParagraphs(newParagraphs);
  };

  // Function to handle submitting revisions
  const handleSubmitRevisions = () => {
    const revisedEssay = paragraphs.join('\n\n');
    setAnswer(revisedEssay);
    setIsRevisionMode(false);
    setStep(4); // Go back to confirm answer step
  };

  const handleSaveEssay = async () => {
    try {
      const subject = getCategorySubject(category);
      
      const response = await fetch(`${API_BASE_URL}/api/essays`, {
        method: 'POST',
        credentials: 'include', 
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question,
          answer,
          category,
          subject,  // Add subject to the request
          visible_to_teacher: shareWithTeacher  // Add this line
        })
      });

      if (response.ok) {
        // Success message with complete information
        alert('Essay saved successfully!\n\nYou can find all your saved essays in the "My Essays" section (green button in the sidebar).');
      } else {
        alert('Failed to save essay. Please try again.');
      }
    } catch (error) {
      logger.error('Error saving essay:', error);
      alert(`Failed to save essay: ${error.message}`);
    }
  };

  const handleReviseClick = () => {
    // Split the text into sections based on feedback comments
    const sections = answer.split(/({[PCLM],\s*.*?})/g).filter(Boolean);
    
    return (
      <div>
        {sections.map((section, index) => {
          // If the section matches a comment pattern, display it as non-editable text
          if (section.match(/^{[PCLM],\s*.*?}$/)) {
            return (
              <div key={index} style={{
                padding: '10px',
                margin: '10px 0',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                fontStyle: 'italic'
              }}>
                {section}
              </div>
            );
          } else {
            // Only render textarea if section has meaningful content and isn't the grade calculation
            if (section.trim().length > 2 && !section.includes("Total Score Calculation")) {
              return (
                <textarea
                  key={index}
                  defaultValue={section}
                  onChange={(e) => {
                    // Track this section's edit
                    setEditedSections(prev => ({
                      ...prev,
                      [index]: e.target.value
                    }));
                    
                    // Update the full answer with all edits
                    const updated = [...sections];
                    updated[index] = e.target.value;
                    // Apply any other tracked edits
                    Object.entries(editedSections).forEach(([idx, value]) => {
                      if (idx !== index.toString()) {
                        updated[idx] = value;
                      }
                    });
                    setAnswer(updated.join(''));
                  }}
                  style={{
                    width: '100%',
                    minHeight: '150px',
                    padding: '10px',
                    marginBottom: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px'
                  }}
                />
              );
            }
            return null;
          }
        })}
        {/* Rest of your step 5 UI */}
      </div>
    );
  };

  const splitTextIntoSections = (text) => {
    // Split on feedback comment blocks that start with {P, {C, or {L
    const sections = text.split(/(\{[PCLM]:.+?\}\n\n)+/);
    
    // Group text with its following comments
    const result = [];
    for (let i = 0; i < sections.length; i += 2) {
      result.push({
        text: sections[i],
        comments: sections[i + 1] || ''
      });
    }
    return result;
  };

  // Add this helper function
  const splitEssayAndComments = (text) => {
    // Split on comment blocks that start with {P:, {C:, or {L:
    const parts = text.split(/(\{[PCLM]:[^}]+\}\n\n)/g);
    const sections = [];
    
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].trim()) {  // Skip empty strings
        if (parts[i].match(/^\{[PCLM]:/)) {
          // This is a comment section
          sections.push({ type: 'comment', content: parts[i] });
        } else {
          // This is essay text
          sections.push({ type: 'text', content: parts[i] });
        }
      }
    }
    return sections;
  };

  // Add this component near the top after the imports
  const FeedbackLegend = () => (
    <div style={{ 
      display: 'flex', 
      gap: '15px', 
      justifyContent: 'center',
      margin: '10px 0',
      padding: '10px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px'
    }}>
      <span style={{ color: '#2ecc71' }}>● Purpose</span>
      <span style={{ color: '#e74c3c' }}>● Coherence</span>
      <span style={{ color: '#3498db' }}>● Language Use</span>
      <span style={{ color: '#f1c40f' }}>● Mechanics</span>
    </div>
  );

  // Add this near the top of the component
  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  // Add this common input style object that can be reused
  const commonInputStyle = {
    width: '100%',
    padding: '12px 16px',
    marginBottom: '15px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    color: '#334155',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    resize: 'vertical'
  };

  // Add hover and focus styles
  const inputHoverStyle = {
    borderColor: '#94a3b8',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
  };

  const inputFocusStyle = {
    outline: 'none',
    borderColor: '#2980b9',
    boxShadow: '0 0 0 3px rgba(41, 128, 185, 0.1)'
  };

  // Add this style object near your other common styles
  const commonSelectStyle = {
    ...commonInputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center',
    backgroundSize: '1em',
    paddingRight: '2.5rem',
    cursor: 'pointer',
    minHeight: '45px',
    backgroundColor: 'white',
    transition: 'all 0.2s ease',
    width: 'auto',
    minWidth: '200px',
    maxWidth: '600px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  // Also add this style for the option elements
  const optionStyle = {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    padding: '8px 12px',
    maxWidth: '600px'
  };

  // Add this common button style near your other style definitions
  const commonButtonStyle = {
    padding: '12px 24px',
    fontSize: '16px',
    backgroundColor: '#2980b9',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    margin: '0 8px'
  };

  // Add a secondary button style for actions like "Back" or "Cancel"
  const secondaryButtonStyle = {
    ...commonButtonStyle,
    backgroundColor: '#95a5a6',
    color: 'white'
  };

  // Add hover effects with event handlers
  const buttonHoverStyle = {
    backgroundColor: '#3498db',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
  };

  const secondaryButtonHoverStyle = {
    backgroundColor: '#7f8c8d',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
  };

  // Modify handleCategorySelect
  const handleCategorySelect = (selectedCategory) => {
    if (selectedCategory === 'Filiocht') {
      setSelectedFiliocht(true);
      setSelectedPoem(null);  // Reset poem selection
      return;
    }
    
    // Reset Filiocht state for all other categories
    setSelectedFiliocht(false);
    setSelectedPoem(null);
    
    if (selectedCategory === 'Grammar Checker' || selectedCategory === 'FR_Grammar' || 
        selectedCategory === 'ES_Grammar' || selectedCategory === 'DE_Grammar') {
      setCategory(selectedCategory);
      setQuestion("Grammar Check");
      setStep(3);
    } else {
      setCategory(selectedCategory);
      setStep(2);
    }
  };

  const handleFiliochtPartSelect = (part) => {
    if (!selectedPoem) return;  // Don't proceed without poem selection
    setCategory(part);
    // Don't set any initial question text
    setStep(2);
    setSelectedFiliocht(null);
  };

  useEffect(() => {
    if (savedEssayData) {
      logger.debug('Loading saved essay data:', savedEssayData);
      
      setQuestion(savedEssayData.question || '');
      setAnswer(savedEssayData.answer || '');
      setCategory(savedEssayData.category || 'Shakespeare');
      
      if (savedEssayData.skipGrading && savedEssayData.result) {
        logger.debug('Loading feedback with task_id:', savedEssayData.task_id);
        setResult(savedEssayData.result);
        setShortTaskId(savedEssayData.task_id);
        setEssayId(savedEssayData.essayId || null);
        
        // Check for math feedback and use startAtStep instead of hardcoding step=5
        if (savedEssayData.isMathFeedback && savedEssayData.startAtStep) {
          logger.debug('Setting maths feedback step to:', savedEssayData.startAtStep);
          setStep(savedEssayData.startAtStep);
        } else {
          logger.debug('Setting regular feedback step to 5');
          setStep(5);
        }
      } else if (savedEssayData.startAtStep) {
        setStep(savedEssayData.startAtStep);
      }
      
      clearSavedEssayData();
    }
  }, [savedEssayData, clearSavedEssayData, setQuestion, setAnswer, setCategory, setResult, setStep]);

  // Also add a debug check in the render section
  logger.debug('Render state:', { step, result, category }); // NEW DEBUG LOG

  // Add debug logging in render
  logger.debug('MainContent render - shortTaskId:', shortTaskId);

  useEffect(() => {
    if (savedEssayData) {
      if (savedEssayData.teacherFeedback) {
        setTeacherFeedback(savedEssayData.teacherFeedback);
      }
      // existing code that sets question, answer, etc.
    }
  }, [savedEssayData]);
  // Also check if any other effects might be resetting shortTaskId
  useEffect(() => {
    if (step === 5) {
      logger.debug('Step changed to 5, shortTaskId:', shortTaskId);
    }
  }, [step, shortTaskId]);

  // Clear localStorage when resetting
  useEffect(() => {
    if (step === 1) {
      localStorage.removeItem('currentShortTaskId');
    }
  }, [step]);

  // Double check shortTaskId whenever step changes to 5
  useEffect(() => {
    if (step === 5) {
      const storedId = localStorage.getItem('currentShortTaskId');
      if (storedId && !shortTaskId) {
        logger.debug('Recovering shortTaskId from localStorage:', storedId);
        setShortTaskId(storedId);
      }
    }
  }, [step, shortTaskId]);

  // Add this validation function near handleSelectImages
  const validateFile = (file) => {
    // File size limit (20MB)
    const MAX_FILE_SIZE_MB = 20;
    const fileSizeMB = file.size / (1024 * 1024);
    
    // Check file type - only allow common image formats
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/tiff', 'image/bmp', 'application/pdf'];
    
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type not supported. Please upload JPG, PNG, TIFF or PDF files.`
      };
    }
    
    // Check file size
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      return {
        valid: false,
        error: `File "${file.name}" is too large (${fileSizeMB.toFixed(1)}MB). Maximum size is ${MAX_FILE_SIZE_MB}MB.`
      };
    }
    
    return { valid: true };
  };

  // Modify the handleSelectImages function
  const handleSelectImages = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    // Validate each file before adding
    const invalidFiles = [];
    const validFiles = [];
    
    files.forEach(file => {
      const validation = validateFile(file);
      if (!validation.valid) {
        invalidFiles.push({ file, error: validation.error });
      } else {
        validFiles.push(file);
      }
    });
    
    // Show error messages for invalid files
    if (invalidFiles.length > 0) {
      // Display errors (customize this based on your UI)
      const errorMessages = invalidFiles.map(item => item.error);
      alert(errorMessages.join('\n'));
    }
    
    // Only add valid files to the state
    if (validFiles.length > 0) {
      setImageFiles(prev => [...prev, ...validFiles]);
    }
  };

  // 2) A small helper for reordering an image up or down
  const moveImageUp = (index) => {
    if (index === 0) return;
    setImageFiles((prev) => {
      const copy = [...prev];
      [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]];
      return copy;
    });
  };

  const moveImageDown = (index) => {
    setImageFiles((prev) => {
      if (index === prev.length - 1) return prev;
      const copy = [...prev];
      [copy[index + 1], copy[index]] = [copy[index], copy[index + 1]];
      return copy;
    });
  };

  // Add function to remove an image
  const removeLocalImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Add shareWithTeacher to the onConfirmAnswer call
  const handlePrevStep = () => {
    if (category?.startsWith('Filiocht_')) {
      // If we're in a Filiocht section, go back to poem selection
      setCategory('Filiocht');
      setSelectedFiliocht(true);
      setStep(1);
    } else {
      prevStep();
    }
  };

  useEffect(() => {
    // Add this debugging effect
    logger.debug('MainContent - userData:', userData);
    logger.debug('User country:', userData?.country);
  }, [userData]);

  // Track step changes
  useEffect(() => {
    if (step > 0) {
      // Map step numbers to descriptive names
      const stepNames = {
        1: 'Category_Selection',
        2: 'Question_Selection',
        3: 'Answer_Input',
        4: 'Answer_Confirmation',
        5: 'Results_View'
      };
      
      track('step_reached', { 
        step: step,
        stepName: stepNames[step] || `Step_${step}`,
        category: category || 'none'
      });
    }
  }, [step, category]);
  
  // Add debug logging for category changes
  useEffect(() => {
    logger.debug('Category changed:', category);
    logger.debug('Current step:', step);
    logger.debug('Is maths category:', isMathCategory(category));
  }, [category, step]);

  // Add an effect to scroll to top when entering step 4 or step 5
  useEffect(() => {
    if (step === 4 || step === 5) {
      // Scroll to the top of the page when entering the confirm answer page or results page
      window.scrollTo(0, 0);
    }

    // Reset state variables when returning to step 4
    if (step === 4) {
      // Reset grammar check in progress flag
      setGrammarCheckInProgress(false);
      // For Irish categories, trigger spellcheck after a brief delay to allow the UI to render first
      const isIrishCategory = category === 'Aiste' || 
                         category === 'Grammar Checker' || 
                         category === 'IE_Oral' || 
                         category.startsWith('Filiocht_');
      
      if (isIrishCategory) {
        // Reset spellcheck state, then enable it after a small delay
        setIsSpellChecking(false);
        setTimeout(() => {
          setIsSpellChecking(true);
        }, 500);
      }
    }
  }, [step, category]);

  // Function to check if category is a math category
  const isMathCategory = (category) => {
    if (!category) return false; // Handle null/undefined category
    return category === 'MATH_Concepts' || category === 'MATH_Applications';
  };

  // Add debug logging for the render condition
  useEffect(() => {
    if (step === 2 && isMathCategory(category)) {
      logger.debug('RENDERING MATHS WORKFLOW:', {
        step,
        category,
        isMath: isMathCategory(category),
        result: result ? 'has result data' : 'no result data'
      });
    }
  }, [step, category, result]);

  // Add a function to check Irish text for grammar issues
  const checkIrishGrammarIssues = async () => {
    // Only check for Irish categories
    const isIrishCategory = category === 'Aiste' ||
                         category === 'Grammar Checker' ||
                         category === 'IE_Oral' ||
                         (typeof category === 'string' && category.startsWith('Filiocht_'));

    if (!isIrishCategory) {
      logger.info("Not an Irish category, skipping grammar check");
      return { proceed: true, message: null }; // No need to check
    }

    logger.info(`Starting grammar check for category: ${category}`);
    setGrammarCheckInProgress(true);

    // Retry logic
    const maxRetries = 2;
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        attempt++;

        const cleanedAnswer = stripInlineAnnotations_for_confirm_answer(answer);

        // Add a dummy parameter to avoid caching
        const timestamp = new Date().getTime();
        const response = await fetch(`${API_BASE_URL}/api/check-irish-text?t=${timestamp}`, {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: cleanedAnswer,
            category: category
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          logger.error(`Grammar check API error on attempt ${attempt}: ${response.status} - ${errorText}`);

          if (attempt <= maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Increase delay
            continue; // Try again
          }

          // Throw error only after max retries failed
          throw new Error(`Failed to check Irish text after ${attempt} attempts: ${response.status}`);
        }

        const data = await response.json();
        logger.debug("Grammar check response:", data);
        setGrammarCheckInProgress(false); // Success, reset flag
        const issueCount = data.issueCount || 0; // Extract issue count

        if (data.warningNeeded) {
          // Force set the warning message directly from the API response
          const warningMessage = `Rabhadh: ${issueCount} grammar issues found. ${data.message}`;
          setGrammarWarning(warningMessage); // Set state for banner display

          // Return proceed: false, the message, and the count
          return { proceed: false, message: warningMessage, issueCount: issueCount };
        } else {
          setGrammarWarning(null); // Clear banner state
          return { proceed: true, message: null, issueCount: 0 }; // No issues
        }
      } catch (error) {
        logger.error(`Error in grammar check (attempt ${attempt}):`, error);

        if (attempt <= maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Wait before retry
          continue; // Retry loop
        }

        // Exhausted retries - return proceed: false with an error message for the dialog
        setGrammarCheckInProgress(false); // Ensure loading state is reset
        const errorMessage = "Error checking grammar after multiple attempts. Do you want to submit anyway?";
        // Set banner warning as well in case the dialog is dismissed unexpectedly
        setGrammarWarning(errorMessage.replace(" Do you want to submit anyway?", ""));
        return {
          proceed: false,
          message: errorMessage,
          issueCount: null // Indicate error state with null count
        };
      }
    }
    // Should not be reached if loop logic is correct, but return true as fallback
    logger.warn("Grammar check loop finished unexpectedly.");
    setGrammarCheckInProgress(false);
    return { proceed: true, message: null, issueCount: 0 };
  };

  // Add this effect to enable spellcheck automatically for Irish categories
  useEffect(() => {
    // Auto-enable spellcheck for Irish categories on step 4
    if (step === 4) {
      const isIrishCategory = category === 'Aiste' || 
                         category === 'Grammar Checker' || 
                         category === 'IE_Oral' || 
                         category.startsWith('Filiocht_');
      
      if (isIrishCategory) {
        setSpellCheckEnabled(true);
        // Automatically trigger spellchecking when loading step 4 for Irish categories
        setIsSpellChecking(true);
      }
    }
  }, [step, category]);

  // Modify the useEffect for spellchecking to run only when triggered by blur
  useEffect(() => {
    // Only run if spellchecking is explicitly enabled
    if (!isSpellChecking) {
      return;
    }
    
    // Only run the effect if we're on step 4 (confirm answer)
    if (step !== 4) {
      setHighlightedAnswer(''); // Clear any highlights when not on step 4
      setIsSpellChecking(false);
      return;
    }
    
    // Check if this is an Irish category that should have spellchecking
    const isIrishCategory = category === 'Aiste' || 
                        category === 'Grammar Checker' || 
                        category === 'IE_Oral' || 
                        category.startsWith('Filiocht_');
    
    if (!isIrishCategory) {
      setIsSpellChecking(false);
      return; // Skip spell checking for non-Irish categories
    }
    
    // If not spellcheck enabled and we're on step 4 and it's an Irish category, 
    // we should enable it automatically
    if (!spellCheckEnabled && isIrishCategory) {
      setSpellCheckEnabled(true); // Enable it for future blur events
    }
    
    // Get the cleaned version of the answer
    const textToCheck = stripInlineAnnotations_for_confirm_answer(answer);
    
    if (!textToCheck.trim()) {
      setHighlightedAnswer('');
      setIsSpellChecking(false);
      return;
    }
    
    // Preserve cursor position if user is actively editing
    let savedSelection = null;
    let activeElement = null;
    
    if (document.activeElement && document.activeElement.id === 'answer-editor') {
      activeElement = document.activeElement;
      
      // Store selection/cursor position
      if (window.getSelection) {
        savedSelection = saveSelection(activeElement);
      }
    }
    
    // Run the spellcheck immediately rather than with debouncing
    (async () => {
      try {
        const results = await checkIrishSpelling(textToCheck, ignoreFadaEnabled);
        
        if (results.error) {
          logger.error('Spellcheck API returned error:', results.error);
          alert(`Spellcheck error: ${results.error}`);
          setIsSpellChecking(false);
          return;
        }
        
        setSpellCheckResults(results);
        
        // Highlight misspelled words in the text
        const highlighted = highlightMisspelledWords(textToCheck, results);
        setHighlightedAnswer(highlighted);
        
        // Restore cursor position after the DOM has been updated
        setTimeout(() => {
          const editorElement = document.getElementById('answer-editor');
          if (activeElement && activeElement.id === 'answer-editor' && editorElement) {
            // Focus the element first
            editorElement.focus();
            
            // Try to restore selection if we had one
            if (savedSelection) {
              restoreSelection(editorElement, savedSelection);
            }
          }
        }, 50); // Slight delay to ensure the DOM is fully updated
      } catch (error) {
        logger.error('Spell check failed:', error);
        // Reset to avoid showing outdated results
        setHighlightedAnswer('');
        setSpellCheckResults(null);
      } finally {
        setIsSpellChecking(false);
      }
    })();
    
    return () => {
      // Cleanup if needed
    };
  }, [isSpellChecking, answer, spellCheckEnabled, step, category, ignoreFadaEnabled]);
  
  // Add a function to render either the highlighted text or normal textarea
  const renderAnswerInput = () => {
    const textToDisplay = stripInlineAnnotations_for_confirm_answer(answer);
    const isIrishCategory = category === 'Aiste' || 
                        category === 'Grammar Checker' || 
                        category === 'IE_Oral' || 
                        category.startsWith('Filiocht_');
    
    // Show loading indicator when spell checking is in progress
    if (isSpellChecking) {
      return (
        <div className="spellcheck-loading">
          <textarea
            id="answer-editor"
            value={textToDisplay}
            onChange={handleAnswerChange}
            placeholder="Write your answer here..."
            className="common-input answer-input"
            disabled={isSpellChecking}
          />
          <div className="spellcheck-overlay">
            <div className="spellcheck-spinner"></div>
            <p>Checking spelling...</p>
          </div>
        </div>
      );
    }
    
    // Updated implementation - use a contentEditable div instead of overlay + textarea
    if (spellCheckEnabled && highlightedAnswer && isIrishCategory) {
      logger.info('Rendering real-time highlighting editable text');
      
      return (
        <div className="spellcheck-editable-container">
          <div
            id="answer-editor"
            contentEditable={true}
            dangerouslySetInnerHTML={{ __html: highlightedAnswer }}
            className="common-input answer-input"
            spellCheck={false}
            onInput={handleContentInput}
            onBlur={handleEditorBlur}
          />
        </div>
      );
    }
    
    // Default textarea when spellcheck is disabled or no highlights
    logger.debug('Rendering default textarea');
    
    // For Irish categories: use our custom spellcheck if enabled, or no spellcheck if disabled
    // For non-Irish categories: use the browser's native spellcheck if the checkbox is enabled
    const shouldEnableNativeSpellcheck = !isIrishCategory && spellCheckEnabled;
    
    return (
      <textarea
        id="answer-editor"
        value={textToDisplay}
        onChange={handleAnswerChange}
        placeholder="Write your answer here..."
        spellCheck={shouldEnableNativeSpellcheck}
        className="common-input answer-input"
        onBlur={isIrishCategory && spellCheckEnabled ? handleEditorBlur : undefined}
      />
    );
  };
  
  // Modified handleAnswerChange to update both answer and word count
  const originalHandleAnswerChange = handleAnswerChange;
  handleAnswerChange = (event) => {
    logger.info('Answer changed');
    
    // Call the original handler to update the state
    originalHandleAnswerChange(event);
    
    // Update word count
    setWordCount(calculateWordCount(event.target.value));
    
    // Don't start spellchecking immediately - this will happen on blur
  };

  // Update handleContentInput to also update word count
  const handleContentInput = (e) => {
    // Get the plain text content (without HTML)
    const newText = e.target.innerText || '';
    
    // Only update if content actually changed to avoid loops
    if (newText !== stripInlineAnnotations_for_confirm_answer(answer)) {
      // Create a new event-like object with required properties
      const syntheticEvent = { target: { value: newText } };
      handleAnswerChange(syntheticEvent);
      
      // Update word count
      setWordCount(calculateWordCount(newText));
    }
  };
  
  // Add a function to trigger spellcheck when user clicks outside the text box
  const handleEditorBlur = () => {
    logger.debug('Editor lost focus - triggering spellcheck');
    // Only trigger spellcheck if it's enabled and we're in an Irish category
    const isIrishCategory = category === 'Aiste' || 
                         category === 'Grammar Checker' || 
                         category === 'IE_Oral' || 
                         category.startsWith('Filiocht_');
                         
    if (spellCheckEnabled && isIrishCategory) {
      // Make sure we get the full text content before spell checking
      const editorElement = document.getElementById('answer-editor');
      if (editorElement) {
        // For contentEditable div, get innerText; for textarea, get value
        const fullText = editorElement.innerText || editorElement.value || '';
        logger.debug(`Editor blur - got ${fullText.length} characters of text`);
        logger.debug('Text sample:', fullText.substring(0, 100) + '...');
        
        // Update the answer state with complete text if using contentEditable
        if (editorElement.contentEditable === 'true' && fullText !== answer) {
          logger.debug('Updating answer state with complete text from contentEditable');
          const syntheticEvent = { target: { value: fullText } };
          handleAnswerChange(syntheticEvent);
        }
      }
      
      setIsSpellChecking(true);
    }
  };

  // Add a helper function to save the current selection/cursor position in contentEditable
  const saveSelection = (containerEl) => {
    if (!containerEl || !window.getSelection) return null;
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return null;
    
    const range = selection.getRangeAt(0);
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(containerEl);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const start = preSelectionRange.toString().length;
    
    return {
      start,
      end: start + range.toString().length,
      text: range.toString()
    };
  };
  
  // Add a helper function to restore the selection/cursor position in contentEditable
  const restoreSelection = (containerEl, savedSelection) => {
    if (!containerEl || !savedSelection || !window.getSelection) return;
    
    const charIndex = (node, index) => {
      let currentIndex = 0;
      if (index <= 0) return { node, position: 0 };
      
      const walk = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null, false);
      let currentNode = walk.nextNode();
      
      while (currentNode) {
        const nodeLength = currentNode.nodeValue.length;
        if (currentIndex + nodeLength >= index) {
          return { 
            node: currentNode, 
            position: index - currentIndex 
          };
        }
        
        currentIndex += nodeLength;
        currentNode = walk.nextNode();
      }
      
      // If we couldn't find the exact position, return the last possible position
      return { 
        node: node, 
        position: node.textContent.length 
      };
    };
    
    const range = document.createRange();
    const selection = window.getSelection();
    
    try {
      const startLocation = charIndex(containerEl, savedSelection.start);
      const endLocation = charIndex(containerEl, savedSelection.end);
      
      range.setStart(startLocation.node, startLocation.position);
      range.setEnd(endLocation.node, endLocation.position);
      
      selection.removeAllRanges();
      selection.addRange(range);
      
      logger.debug('Selection restored successfully');
    } catch (err) {
      logger.error('Error restoring selection:', err);
    }
  };

  // Render confirm buttons group with added grammar exemption option
  const ConfirmButtonsGroup = () => {
    // Check if it's an English category where grammar exemption would apply
    const isEnglishCategory = category === 'Poetry' || 
                             category === 'Shakespeare' || 
                             category === 'Comparative' || 
                             category === 'Composition' ||
                             category.startsWith('GCSE_');
                             
    // Define isIrishCategory to avoid the undefined error                         
    const isIrishCategory = category === 'Aiste' || 
                         category === 'Grammar Checker' || 
                         category === 'IE_Oral' || 
                         category.startsWith('Filiocht_');

    return (
      <div className="confirm-buttons-group">
        <div className="confirm-options">
          {/* Share with teacher option */}
          {userData && userData.role === 'student' && userData.teacher_email && (
            <div className="share-option">
              <label>
                <input
                  type="checkbox"
                  checked={shareWithTeacher}
                  onChange={(e) => {
                    setShareWithTeacher(e.target.checked);
                    // Track share with teacher toggle
                    track('checkbox_toggled', {
                      feature: 'share_with_teacher',
                      enabled: e.target.checked,
                      category: category,
                      step: 'confirm_answer',
                      teacher_email: userData?.teacher_email || 'not_available'
                    });
                  }}
                />
                Share with teacher ({userData.teacher_email})
              </label>
            </div>
          )}
          
          {/* Grammar exemption option - only for English categories */}
          {isEnglishCategory && (
            <div className="exemption-option">
              <label>
                <input
                  type="checkbox"
                  checked={grammarExemption}
                  onChange={(e) => {
                    logger.debug('Grammar exemption checkbox changed:', e.target.checked);
                    setGrammarExemption(e.target.checked);
                    setUserChangedExemption(true); // Mark that the user has manually changed this setting
                    // Track grammar exemption toggle in ConfirmButtonsGroup
                    track('checkbox_toggled', {
                      feature: 'grammar_exemption',
                      enabled: e.target.checked,
                      category: category,
                      step: 'confirm_answer',
                      component: 'confirm_buttons_group',
                      user_default: !!userData?.grammar_exemption
                    });
                  }}
                  id="grammar-exemption-checkbox"
                />
                <span>Grammar exemption (no mechanics score)</span>
                <span 
                  className="tooltip-icon" 
                  title="Enable this option if you have spelling/grammar difficulties or dyslexia. Grammar and spelling will be ignored in assessment. You can set a default exemption for all your essays in your account settings."
                >ⓘ</span>
              </label>
              <div style={{ 
                fontSize: '12px', 
                color: '#666',
                marginTop: '4px',
                marginLeft: '25px' 
              }}>
                {userData?.grammar_exemption ? 
                  "Based on your account settings, this option is enabled by default." : 
                  ""}
              </div>
            </div>
          )}
          
          {/* Ignore fada option - only for Irish categories */}
          {isIrishCategory && (
            <div className="fada-option">
              <label>
                <input
                  type="checkbox"
                  checked={ignoreFadaEnabled}
                  onChange={(e) => {
                    setIgnoreFadaEnabled(e.target.checked);
                    // Track ignore fada toggle in ConfirmButtonsGroup
                    track('checkbox_toggled', {
                      feature: 'ignore_fada',
                      enabled: e.target.checked,
                      category: category,
                      step: 'confirm_answer',
                      component: 'confirm_buttons_group' 
                    });
                  }}
                />
                Ignore fada errors
              </label>
            </div>
          )}
        </div>
        
        <div className="feedback-type-buttons">
          <button onClick={() => handleConfirmClick("inline")} disabled={isLoading || grammarCheckInProgress}>
            Get Feedback
          </button>
        </div>
      </div>
    );
  };

  // Add function to calculate word count
  const calculateWordCount = (text) => {
    if (!text) return 0;
    
    // Strip any annotations before counting
    const cleanText = stripInlineAnnotations_for_confirm_answer(text);
    
    // Split by whitespace and filter out empty strings
    const words = cleanText.split(/\s+/).filter(word => word.length > 0);
    return words.length;
  };

  // Add effect to calculate word count when answer changes
  useEffect(() => {
    if (answer) {
      setWordCount(calculateWordCount(answer));
    }
  }, [answer]);

  // Effect to reset comprehension creation state when leaving the view
  useEffect(() => {
    if (step !== 1 || category !== 'Comprehension' || userData?.role !== 'teacher') {
        if (isCreatingComprehension) {
            logger.debug("Resetting isCreatingComprehension state.");
            setIsCreatingComprehension(false);
        }
    }
  }, [step, category, userData?.role, isCreatingComprehension]); // Add isCreatingComprehension to deps

  // Add the handler function for submission completion
  const handleSubmissionComplete = (submissionData) => {
    logger.info("Submission complete, setting viewing data:", submissionData);
    // We need more than just status, need details for the view
    // For now, let's store the full status data which includes score/feedback
    // We might need to fetch comprehension details separately if needed by ComprehensionSubmissionsView
    setViewingSubmissionData(submissionData); 
    // Optionally, force step back to 1 if not already there to trigger the view switch
    setStep(1); // <-- Uncomment this line
  };

  // Clear viewingSubmissionData when resetting
  useEffect(() => {
    if (step === 1 && viewingSubmissionData) {
      // Reset view when user navigates away or resets
      // setViewingSubmissionData(null);
      // Consider if reset is needed or if it should persist until explicit back
    }
  }, [step, viewingSubmissionData]);

  // Determine if we should render QuizWorkflow directly
  const shouldRenderQuizWorkflow = 
    step === 1 && 
    (category === 'Biology' || category === 'Chemistry') && 
    (selectedBranch === 'Biology' || selectedBranch === 'Chemistry' || currentQuizId !== null);

  return (
    <div className="main-content">
      <div className="container">
        {/* Add the custom confirm dialog to the DOM */}
        <CustomConfirmDialog />
      
        {/* STEP 1: Category Selection OR Comprehension View */}
        {step === 1 && category === 'Comprehension' && (
          // Render different views based on user role or if viewing a specific submission
          userData?.role === 'teacher' ? (
            isCreatingComprehension ? (
              <ComprehensionUpload
                API_BASE_URL={API_BASE_URL}
                authToken={authToken}
                userData={userData}
                onBack={() => setIsCreatingComprehension(false)}
              />
            ) : (
              <ComprehensionSubmissionsView // Use new name
                API_BASE_URL={API_BASE_URL}
                authToken={authToken}
                userData={userData}
                isCreating={isCreatingComprehension}
                setIsCreating={setIsCreatingComprehension}
                initialSubmissionData={null} // Explicitly null for teacher list view
              />
            )
          ) : viewingSubmissionData ? (
             <ComprehensionSubmissionsView // Use new name
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              isCreating={false} // Not creating when viewing submission
              setIsCreating={() => {}} // Dummy setter
              initialSubmissionData={viewingSubmissionData} // Pass submission data
              // Add a callback to clear the view when user clicks back inside this view
              onBack={() => setViewingSubmissionData(null)} 
            />
          ) : (
            <ComprehensionView 
              API_BASE_URL={API_BASE_URL} 
              authToken={authToken} 
              userData={userData} 
              // Pass the callback prop
              onSubmissionComplete={handleSubmissionComplete}
            />
          )
        )}

        {/* Render QuizWorkflow if conditions are met */}
        {shouldRenderQuizWorkflow ? (
          <QuizWorkflow
            API_BASE_URL={API_BASE_URL}
            authToken={authToken}
            userData={userData}
            subject={category} // Pass the specific subject (Biology/Chemistry)
            // Pass quizId and attemptId if available from Dashboard click
            initialQuizId={currentQuizId} 
            initialAttemptId={currentAttemptId}
            onBack={() => {
              // Reset quiz-specific state in App.js if necessary, 
              // or just navigate back to category/subject selection.
              // For now, just clear local category and let App.js handle global state if needed.
              setCategory(null); 
              // Clear quiz state to prevent it from persisting into fresh workflows
              if (typeof clearQuizState === 'function') {
                clearQuizState();
              }
              setStep(1); // Go back to general category selection
              // Potentially call a function passed from App.js to clear quiz state there
              if (setSelectedBranch) setSelectedBranch(null); // Clear selected branch in App.js if used this way
            }}
          />
        ) : null}

        {/* Render StudentNotesWorkflow for Custom category */}
        {step === 1 && category === 'Custom' && (
          <StudentNotesWorkflow
            API_BASE_URL={API_BASE_URL}
            authToken={authToken}
            userData={userData}
            onBack={() => {
              setCategory(null);
              setStep(1);
              if (setSelectedBranch) setSelectedBranch(null);
            }}
          />
        )}

        {/* Render CategorySelection for other cases */}
        {step === 1 && category !== 'Comprehension' && category !== 'Biology' && category !== 'Chemistry' && category !== 'Custom' && (
          <CategorySelection
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            selectedFiliocht={selectedFiliocht}
            setSelectedFiliocht={setSelectedFiliocht}
            selectedPoem={selectedPoem}
            setSelectedPoem={setSelectedPoem}
            setCategory={setCategory}
            setQuestion={setQuestion}
            setStep={setStep}
            usageRemaining={usageRemaining}
            userCountry={userData?.country}
            userData={userData}
          />
        )}
        
        {/* Add special handling for Math categories */}
        {step === 2 && isMathCategory(category) && (
          <MathWorkflow
            category={category}
            setCategory={setCategory}
            setStep={setStep}
            handleResetApp={handleResetApp}
            API_BASE_URL={API_BASE_URL}
            authToken={authToken}
            setResult={setResult}
            userData={userData}
            result={result}
            shortTaskId={shortTaskId}
          />
        )}
        
        {/* Keep existing step 2 for non-math categories */}
        {step === 2 && !isMathCategory(category) && (
          <QuestionSelect
            category={category}
            question={question}
            setQuestion={setQuestion}
            handlePrevStep={handlePrevStep}
            handleQuestionSubmit={handleQuestionSubmit}
            selectedPoem={selectedPoem}
            API_BASE_URL={API_BASE_URL}
            authToken={authToken}
            userData={userData}
          />
        )}

        {/* STEP 3: Image Upload */}
        {step === 3 && !isMathCategory(category) && (
          <div data-step="3">
            <h2>{category}</h2>
            <h3>Upload Your Answer</h3>
            
            {/* OCR Guide Box */}
            <div className="ocr-guide">
              <h4 className="ocr-guide-title">📸 Tips for Best Scan Results</h4>
              <ul className="ocr-guide-list">
                <li className="ocr-guide-item">
                  <span className="ocr-guide-icon">✂️</span>
                  <span>Crop your photo to show only the text you want scanned - avoid including parts of other pages</span>
                </li>
                <li className="ocr-guide-item">
                  <span className="ocr-guide-icon">👁️</span>
                  <span>Ensure all text is clearly visible - any omitted or cut-off words cannot be scanned</span>
                </li>
                <li className="ocr-guide-item">
                  <span className="ocr-guide-icon">📱</span>
                  <span>Take photos in good lighting and keep the camera steady for sharp images</span>
                </li>
                <li className="ocr-guide-item">
                  <span className="ocr-guide-icon">⚠️</span>
                  <span>Note: Our scanner might include crossed-out words or teacher's comments, and may split sentences between pages</span>
                </li>
                <li className="ocr-guide-item">
                  <span className="ocr-guide-icon">✅</span>
                  <span>Don't worry - you can fix any scanning mistakes in the next step!</span>
                </li>
              </ul>
            </div>

            <div className="upload-section">
              <label className="upload-button">
                {imageFiles.length === 0 ? 'Upload Pages' : 'Upload Another Page'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleSelectImages}
                />
              </label>
            </div>
            
            {imageFiles.length > 0 && (
              <div className="uploaded-images">
                <h4>Your Uploaded Images</h4>
                <ul className="image-list">
                  {imageFiles.map((file, index) => (
                    <li key={index} className="image-item">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Page ${index + 1}`}
                        className="image-preview"
                      />
                      <div className="image-info">
                        <div className="image-title">Page {index + 1}</div>
                        <div className="image-filename">{file.name}</div>
                      </div>
                      <div className="image-controls">
                        <button
                          onClick={() => moveImageUp(index)}
                          disabled={index === 0}
                          className="image-control-button"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveImageDown(index)}
                          disabled={index === imageFiles.length - 1}
                          className="image-control-button"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => removeLocalImage(index)}
                          className="image-control-button remove"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="button-container">
              <button onClick={handleSubmitAnswer} className="submit-button">
                Submit 
              </button>
              <button onClick={handlePrevStep} className="secondary-button">
                Back
              </button>
              <button onClick={nextStep} className="common-button">
                Type Your Answer
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Confirm Answer */}
        {step === 4 && (
          <div>
            <h2>Confirm Answer</h2>
            
            {/* Word Count Display */}
            <div className="word-count-display" style={{
              textAlign: 'right',
              color: '#666',
              fontSize: '14px',
              marginBottom: '10px'
            }}>
              Word Count: {wordCount}
            </div>
            
            {/* Grammar warning at the top for high visibility */}
            {grammarWarning && (
              <div className="grammar-warning" style={{ 
                color: '#e74c3c', 
                padding: '15px', 
                margin: '15px 0', 
                backgroundColor: '#ffebee',
                border: '2px solid #e74c3c',
                borderRadius: '4px',
                fontWeight: 'bold',
                fontSize: '16px',
                textAlign: 'center'
              }}>
                <span style={{ fontSize: '20px', marginRight: '8px' }}>⚠️</span>
                {grammarWarning}
              </div>
            )}
            
            <div className="spellcheck-container">
              <label className="spellcheck-label">
                <input
                  type="checkbox"
                  checked={spellCheckEnabled}
                  onChange={(e) => {
                    setSpellCheckEnabled(e.target.checked);
                    // Track spellcheck toggle
                    track('checkbox_toggled', {
                      feature: 'spellcheck',
                      enabled: e.target.checked,
                      category: category,
                      step: 'confirm_answer'
                    });
                  }}
                  className="spellcheck-checkbox"
                />
                {category === 'Aiste' || 
                category === 'Grammar Checker' || 
                category === 'IE_Oral' || 
                category.startsWith('Filiocht_') ? "Enable Spellcheck" : "Enable Spellcheck"}
              </label>
              {(category === 'Aiste' || 
                category === 'Grammar Checker' || 
                category === 'IE_Oral' || 
                category.startsWith('Filiocht_')) && spellCheckEnabled && (
                <label className="spellcheck-label spellcheck-option">
                  <input
                    type="checkbox"
                    checked={ignoreFadaEnabled}
                    onChange={(e) => {
                      setIgnoreFadaEnabled(e.target.checked);
                      // Track ignore fada toggle
                      track('checkbox_toggled', {
                        feature: 'ignore_fada',
                        enabled: e.target.checked,
                        category: category,
                        step: 'confirm_answer'
                      });
                    }}
                    className="spellcheck-checkbox"
                  />
                  Ignore fada differences
                  <span className="tooltip-icon" title="When enabled, words that differ only by accents (fadas) will not be marked as misspelled">ℹ️</span>
                </label>
              )}
              {isSpellChecking && (
                <span className="loading-text"><div className="spinner"></div> Checking...</span>
              )}
              {spellCheckEnabled && !isSpellChecking && (
                <span className="spellcheck-tip">
                  {(() => {
                    const isIrishCategory = category === 'Aiste' || 
                                        category === 'Grammar Checker' || 
                                        category === 'IE_Oral' || 
                                        category.startsWith('Filiocht_');
                    if (isIrishCategory) {
                      return "Spellcheck will run when you click outside the text box. Double click on a word to get suggestions. Note that hyphens and English words, as well as missing fadas may register as errors.";
                    } else {
                      return " ";
                    }
                  })()}
                </span>
              )}
            </div>

            {userData?.role === 'student' && (
              <div className="share-toggle-container">
                <label className="share-toggle-label">
                  <input
                    type="checkbox"
                    checked={shareWithTeacher}
                    onChange={(e) => {
                      setShareWithTeacher(e.target.checked);
                      // Track share with teacher toggle
                      track('checkbox_toggled', {
                        feature: 'share_with_teacher',
                        enabled: e.target.checked,
                        category: category,
                        step: 'confirm_answer',
                        teacher_email: userData?.teacher_email || 'not_available'
                      });
                    }}
                    className="share-toggle-checkbox"
                  />
                  Share With Teacher
                </label>
              </div>
            )}

            {/* Add grammar exemption checkbox for English categories */}
            {(category === 'Poetry' || 
              category === 'Shakespeare' || 
              category === 'Comparative' || 
              category === 'Composition' ||
              category.startsWith('GCSE_')) && (
              <div className="exemption-toggle-container" style={{ marginTop: '10px' }}>
                <label className="exemption-toggle-label">
                  <input
                    type="checkbox"
                    checked={grammarExemption}
                    onChange={(e) => {
                      logger.debug('Grammar exemption checkbox changed:', e.target.checked);
                      setGrammarExemption(e.target.checked);
                      setUserChangedExemption(true); // Mark that the user has manually changed this setting
                      // Track grammar exemption toggle
                      track('checkbox_toggled', {
                        feature: 'grammar_exemption',
                        enabled: e.target.checked,
                        category: category,
                        step: 'confirm_answer',
                        component: 'confirm_buttons_group',
                        user_default: !!userData?.grammar_exemption
                      });
                    }}
                    className="exemption-toggle-checkbox"
                    id="grammar-exemption-checkbox"
                  />
                  <span>Grammar & Spelling Exemption</span>
                  <span className="tooltip-icon" title="Enable this option if you have spelling/grammar difficulties or dyslexia. Grammar and spelling will be ignored in assessment.">ⓘ</span>
                </label>
                {userData?.grammar_exemption && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    marginTop: '4px',
                    marginLeft: '25px' 
                  }}>
                    Based on your account settings, this option is enabled by default.
                  </div>
                )}
              </div>
            )}

            {/* Replace the textarea with the new rendering function */}
            {renderAnswerInput()}
            
            <div className="button-container">
              <button 
                onClick={() => handleConfirmClick('inline')} 
                className="submit-button"
                disabled={isLoading || grammarCheckInProgress}
              >
                {isLoading ? 'Processing...' : grammarCheckInProgress ? 'Checking...' : 'Submit for Feedback'}
              </button>
              <button onClick={handleSaveEssay} className="common-button">
                Save Draft 
              </button>
              <button onClick={handlePrevStep} className="secondary-button">
                Back
              </button>
            </div>
          </div>
        )}

        {/* STEP 5: Results */}
        {step === 5 && result && (
          <ResultAndRevision
            question={question}
            result={result}
            usageRemaining={usageRemaining}
            feedbackType="inline"
            answer={answer}
            setAnswer={setAnswer}
            handleResetApp={handleResetApp}
            handleSavePDF={handleSavePDF}
            feedbackSubmitted={feedbackSubmitted}
            handleFeedbackSubmit={handleFeedbackSubmit}
            feedbackCategory={feedbackCategory}
            setFeedbackCategory={setFeedbackCategory}
            feedbackText={feedbackText}
            setFeedbackText={setFeedbackText}
            isRevising={isRevising}
            setIsRevising={setIsRevising}
            setStep={setStep}
            editedSections={editedSections}
            setEditedSections={setEditedSections}
            scrollToTop={scrollToTop}
            category={category}
            taskId={dbTaskId || shortTaskId|| essayId}
            authToken={authToken}
            API_BASE_URL={API_BASE_URL}
            essayId={essayId}
            userData={userData}
            teacherFeedback={teacherFeedback}
            initialTeacherFeedback={savedEssayData?.teacherFeedback}
            wordCount={wordCount}
          />
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default MainContent;


